<script lang="ts" setup>
  import { type PropType } from 'vue';

  interface OrderShipment {
    externalId: string,
    status: {
      label: string,
      description: string,
    },
    trackingUrl: string | null,
    updatedAt: string,
    parcels: OrderShipmentParcel[]
  }

  interface OrderShipmentParcel {
    externalId: string,
    status: {
      label: string,
      description: string,
    },
    trackingUrl: string | null,
    updatedAt: string,
  }

  defineProps({
    shipment: { type: Object as PropType<OrderShipment>, required: true },
  });
</script>
<template>
  <SfListCard class="shipment-card">
    <div class="shipment-wrapper">
      <div class="shipment">
        <h5 class="shipment-header">
          <span class="icon">
            <SfIconBoxes />
          </span>
          <small>{{ $t('SHOP.ORDER.LABEL.SHIPMENT_NR') }}</small>
          <b>{{ shipment.externalId }}</b>
        </h5>
        <div class="shipment-status">
          <dd class="status-label">{{ shipment.status.label }}</dd>
          <dt class="status-description">{{ shipment.status.description }}</dt>
        </div>
        <div class="shipment-actions">
          <div class="shipment-status-date">
            <SfDateTime :title="$t('SHOP.ORDER.LABEL.SHIPMENT_STATUS_UPDATE_DATE')" :value="shipment.updatedAt" time />
          </div>
          <SfButton v-if="shipment.trackingUrl" :href="shipment.trackingUrl" small target="_blank">
            <template #before>
              <SfIconCrosshair />
            </template>
            <template #default>
              {{ $t('SHOP.ORDER.LABEL.SHIPMENT_CHECK_STATUS') }}
            </template>
          </SfButton>
        </div>
      </div>
      <div class="parcels">
        <SfListCard v-for="parcel in shipment.parcels" :key="parcel.externalId" class="parcel-card">
          <div class="parcel">
            <h6 class="parcel-header">
              <span class="icon">
                <SfIconBoxSeam />
              </span>
              <small>{{ $t('SHOP.ORDER.LABEL.PACKAGE_NR') }}</small>
              <b>{{ parcel.externalId }}</b>
            </h6>
            <div class="parcel-status">
              <dd class="status-label">{{ parcel.status.label }}</dd>
              <dt class="status-description">{{ parcel.status.description }}</dt>
            </div>
            <div class="parcel-actions">
              <div class="parcel-status-date">
                <SfDateTime :title="$t('SHOP.ORDER.LABEL.SHIPMENT_STATUS_UPDATE_DATE')" :value="parcel.updatedAt" time />
              </div>
              <SfButton v-if="parcel.trackingUrl" :href="parcel.trackingUrl" small target="_blank">
                <template #before>
                  <SfIconCrosshair />
                </template>
                <template #default>
                  {{ $t('SHOP.ORDER.LABEL.SHIPMENT_CHECK_STATUS') }}
                </template>
              </SfButton>
            </div>
          </div>
        </SfListCard>
      </div>
    </div>
  </SfListCard>
</template>
<style lang="scss" scoped>
  .shipment-card {
    background: var(--color-light-background);
    border-radius: 1.25rem;

    :deep(.list-card__wrapper) {
      padding: 0.5rem;
    }
  }

  .parcel-card {
    border-radius: 0.75rem;
  }

  .shipment-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .shipment {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 0 calc(0.5rem + 2px);
    gap: 1rem;

    .shipment-header {
      display: grid;
      grid-template-columns: 1.5rem auto;
      grid-template-rows: 1fr 1fr;
      flex-wrap: wrap;
      align-items: center;
      column-gap: 1rem;
      row-gap: 0.125rem;

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        grid-column: 1 / 2;
        grid-row: 1 / 3;

        svg {
          font-size: 1.5rem;
        }
      }

      small {
        font-weight: var(--font-weight-regular);
        font-size: 0.75rem;
        line-height: 1rem;
        grid-column: 2 / 3;
        grid-row: 1 / 2;
      }

      b {
        font-size: 1rem;
        line-height: 1rem;
        grid-column: 2 / 3;
        grid-row: 2 / 3;
      }
    }

    .shipment-status {
      display: flex;
      flex: 1 1 auto;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem 1rem;

      .status-label {
        background: var(--primary-background);
        padding: 0.125rem 1rem;
        font-size: 0.75rem;
        font-weight: var(--font-weight-bold);
        color: var(--primary-foreground);
      }

      .status-description {
        font-size: 0.75rem;
        color: var(--secondary-color);
      }
    }

    .shipment-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 1rem;

      .shipment-status-date {
        flex: 0 0 auto;
        font-size: 0.875rem;
        font-weight: var(--font-weight-bold);
      }
    }
  }

  .parcels {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    .parcel {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 1rem;

      .parcel-header {
        display: grid;
        grid-template-columns: 1.5rem auto;
        grid-template-rows: 1fr 1fr;
        flex-wrap: wrap;
        align-items: center;
        column-gap: 1rem;
        row-gap: 0.125rem;

        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          grid-column: 1 / 2;
          grid-row: 1 / 3;

          svg {
            font-size: 1.25rem;
          }
        }

        small {
          font-weight: var(--font-weight-regular);
          font-size: 0.75rem;
          line-height: 1rem;
          grid-column: 2 / 3;
          grid-row: 1 / 2;
        }

        b {
          font-size: 0.875rem;
          line-height: 1rem;
          grid-column: 2 / 3;
          grid-row: 2 / 3;
        }
      }

      .parcel-status {
        display: flex;
        flex: 1 1 auto;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem 1rem;

        .status-label {
          background: var(--primary-background);
          padding: 0.125rem 1rem;
          font-size: 0.75rem;
          font-weight: var(--font-weight-bold);
          color: var(--primary-foreground);
        }

        .status-description {
          font-size: 0.75rem;
          color: var(--secondary-color);
        }
      }

      .parcel-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 1rem;

        .parcel-status-date {
          flex: 0 0 auto;
          font-size: 0.875rem;
          font-weight: var(--font-weight-bold);
        }
      }
    }
  }
</style>
